import { UntitledIcon } from '@faceup/icons'
import { ulTrash03 } from '@faceup/icons/ulTrash03'
import { UserProvider } from '@faceup/member'
import { Chat, ChatInput, useUnreadFollowUpMessages } from '@faceup/report'
import { Link, Navigate, type RouteCallback, useNavigate } from '@faceup/router'
import { Card } from '@faceup/ui'
import { Avatar, Button, Col, Row, Skeleton, Space, Typography } from '@faceup/ui-base'
import { ChannelType, compact } from '@faceup/utils'
import { type ComponentProps, type ReactNode, Suspense, useContext, useMemo } from 'react'
import PageTitle from '../../Components/PageTitle'
import { LayoutContext } from '../../Contexts/LayoutContext'
import { HideMedia } from '../../mq'
import { defineMessages, FormattedMessage, useIntl } from '../../TypedIntl'
import useAnalytics from '../../utils/analytics'
import useMobileFileDownload from '../../utils/useMobileFileDownload'
import useReportAuth from '../../utils/useReportAuth'

const { Title } = Typography

const messages = defineMessages({
  deletedTitle: 'FollowUp.report.deleted.title',
  deletedButton: 'FollowUp.report.deleted.button',
  messages: 'Shared.chat.title',
  reopen: 'FollowUp.ReportStatusAlert.reopen',
  empty: 'FollowUp.chat.empty',
  writeNote: 'FollowUp.chat.write',
  noteLabel: 'Shared.chat.label',
  administrator: 'FollowUp.chat.administrator',
  leaveQuestion: 'FollowUp.chat.leaveQuestion',
  checkAnotherReport: 'FollowUp.report.checkAnotherReport',
  addAttachments: 'Shared.report.attachments.add',
  attachmentsTitle: 'Shared.report.attachments',
  detectLanguage: 'Shared.report.translate.detectLanguage',
  translate: 'Shared.report.translate.translate',
  showOriginal: 'Shared.report.translate.showOriginal',
  to: 'Shared.report.translate.to',
  sameLanguageError: 'Shared.report.translate.error.sameLanguages',
  disabledReasonE2EE: 'Shared.report.translate.error.disabledReasonE2EE',
  onUseWarningTitle: 'Shared.report.translate.onUseWarning.title',
  onUseWarningContent: 'Shared.report.translate.onUseWarning.content',
  onUseWarningDoNotShowAgain: 'Shared.report.translate.onUseWarning.doNotShowAgain',
})

const closedMessagesPerChannel = defineMessages<ChannelType>({
  [ChannelType.Survey]: 'FollowUp.ReportStatusAlert.submissionWasClosed',
  [ChannelType.AutomatedHotline]: 'FollowUp.ReportStatusAlert.reportWasClosed',
  [ChannelType.LiveHotline]: 'FollowUp.ReportStatusAlert.reportWasClosed',
  [ChannelType.AiHotline]: 'FollowUp.ReportStatusAlert.reportWasClosed',
  [ChannelType.ReportingSystem]: 'FollowUp.ReportStatusAlert.reportWasClosed',
})

const archivedMessagesPerChannel = defineMessages<ChannelType>({
  [ChannelType.Survey]: 'FollowUp.ReportStatusAlert.submissionWasArchived',
  [ChannelType.AutomatedHotline]: 'FollowUp.ReportStatusAlert.reportWasArchived',
  [ChannelType.LiveHotline]: 'FollowUp.ReportStatusAlert.reportWasArchived',
  [ChannelType.AiHotline]: 'FollowUp.ReportStatusAlert.reportWasArchived',
  [ChannelType.ReportingSystem]: 'FollowUp.ReportStatusAlert.reportWasArchived',
})

type FollowUpContentProps = {
  checkRoute: RouteCallback
  title: ReactNode
  loading: boolean
  infoPanel: ReactNode
  viewer: {
    id: string
    case?: {
      id: string
      isDeleted: boolean
      status: { id: string; groupName: string }
      channel: { id: string; sourceType: ChannelType; defaultLanguage: string }
    } | null
  } | null
  followUpItems: Parameters<typeof useUnreadFollowUpMessages>[0]
}

export const FollowUpContent = ({
  checkRoute,
  title,
  loading,
  infoPanel,
  viewer,
  followUpItems,
}: FollowUpContentProps) => {
  const { logout, isAuthenticated } = useReportAuth()
  const { client } = useContext(LayoutContext)
  const navigate = useNavigate()
  const fileDownloadHandler = useMobileFileDownload()
  const { formatMessage } = useIntl()
  const { track } = useAnalytics()

  const aCase = viewer?.case ?? null

  if (!viewer || !aCase) {
    return <Skeleton />
  }

  if (!isAuthenticated()) {
    return <Navigate to={checkRoute} replace />
  }

  const sizeSettings = {
    lg: 24,
    xs: 24,
  }

  if (aCase.isDeleted) {
    return (
      <div className='flex flex-col items-center gap-8'>
        <Avatar size={80}>
          <UntitledIcon size={32} icon={ulTrash03} />
        </Avatar>
        <Title level={2} className='text-center'>
          <FormattedMessage {...messages.deletedTitle} />
        </Title>
        <Link to={checkRoute}>
          <Button ghost type='primary'>
            <FormattedMessage {...messages.deletedButton} />
          </Button>
        </Link>
      </div>
    )
  }

  return (
    <UserProvider
      member={null}
      application='follow-up'
      userId={viewer?.id ?? ''}
      onChangeLanguage={() => null}
      onLogout={() => null}
    >
      <Row justify='center'>
        <Col {...sizeSettings}>
          <PageTitle>{title}</PageTitle>
        </Col>
      </Row>
      <Row justify='center'>
        <Col {...sizeSettings} style={{ paddingBottom: '1em' }}>
          {infoPanel}
        </Col>
      </Row>
      <Row justify='center'>
        <Col {...sizeSettings}>
          <Card loading={loading} className='p-6'>
            <Row gutter={24} style={{ marginBottom: 16 }}>
              <Col span={24} style={{ display: 'flex', alignItems: 'center' }}>
                <Space size={24}>
                  <Title level={2} style={{ display: 'inline-block' }}>
                    <FormattedMessage {...messages.messages} />
                  </Title>
                </Space>
              </Col>
            </Row>
            {/* Chat uses useSuspenseQuery; isolate suspending so the
                route-level fallback doesn't swap the whole page when
                language switches or initial chat data is fetched. */}
            <Suspense fallback={null}>
              <ChatWrapper
                onMessageSend={() => track('Follow-up message sent')}
                followUpItems={followUpItems}
                caseId={aCase.id}
                userId={viewer.id}
                relativeMessages={{
                  type: 'Victim',
                  ...messages,
                  closedMessagesPerChannel,
                  archivedMessagesPerChannel,
                }}
                {...(client === 'app' && { fileDownloadHandler })}
                input={props => (
                  <ChatInput
                    {...props}
                    inputRef={null}
                    mentions={null}
                    placeholder={formatMessage(messages.writeNote)}
                  />
                )}
              />
            </Suspense>
          </Card>
        </Col>
      </Row>
      <HideMedia hideWhen='lgUp'>
        <Row style={{ marginTop: 20 }}>
          <Col {...sizeSettings}>
            <div className='flex justify-center'>
              <Button
                type='link'
                onClick={() => {
                  logout()
                  navigate(checkRoute, { replace: true })
                }}
              >
                <FormattedMessage {...messages.checkAnotherReport} />
              </Button>
            </div>
          </Col>
        </Row>
      </HideMedia>
    </UserProvider>
  )
}

type ChatWrapperProps = {
  followUpItems: FollowUpContentProps['followUpItems']
} & Omit<ComponentProps<typeof Chat>, 'firstUnreadMessageId'>

const ChatWrapper = ({ followUpItems, ...props }: ChatWrapperProps) => {
  const { track } = useAnalytics()

  const comments = useMemo(() => compact(followUpItems ?? []), [followUpItems])
  const { firstUnreadMessageId } = useUnreadFollowUpMessages(comments)
  return (
    <Chat
      {...props}
      firstUnreadMessageId={firstUnreadMessageId ?? ''}
      onRatingChange={rating => track('Report satisfaction rating submitted', { rating })}
    />
  )
}
